/**
 * Mapowanie współrzędnych punktów pomiarowych lakieru dla różnych typów pojazdów
 *
 * UWAGA: Współrzędne są tymczasowe i będą wymagały dostosowania
 * po otrzymaniu właściwych plików SVG dla widoków pojazdu.
 *
 * Współrzędne są w procentach (0-100%) względem viewBox SVG pojazdu.
 */

import {
  VehiclePaintPointsMap,
  VehicleType,
  PaintPointNumber,
} from './VehiclePaintPoints.types';

/**
 * Mapowanie punktów dla sedana
 *
 * Numeracja na podstawie obrazka:
 * - Lewa strona: 12-19 (od przodu do tyłu, góra i dół)
 * - Prawa strona: 20-27 (od przodu do tyłu, góra i dół)
 * - Góra: 28-32 (komora silnika, wzmocnienia)
 */
const sedanPaintPointsMap: VehiclePaintPointsMap = {
  // STRONA LEWA
  left: {
    '8': { x: 50, y: 31, position: 'top' }, // 600-700 (pomarańczowy, góra przednia)
    '2': { x: 81, y: 38, position: 'left' }, // 0-50 (zielony)
    '9': { x: 15, y: 46, position: 'right' }, // BRAK (szary)
    '4': { x: 94, y: 54, position: 'top' }, // 150-200 (żółty, dół tył)
    '10': { x: 35, y: 52, position: 'left' }, // 250-300 (pomarańczowy, koło tylne)
    '11': { x: 15, y: 62, position: 'right' }, // 600-700 (pomarańczowy)
    '7': { x: 92, y: 69, position: 'left' }, // 400-500 (żółty)
    '12': { x: 50, y: 73, position: 'bottom' }, // 150-200 (żółty, koło tylne dół)
    // '15': { x: 62, y: 34, position: 'top' }, // Dodatkowy punkt
  },
  // WIDOK Z GÓRY
  top: {
    '28': { x: 2.3, y: 51, position: 'top', displayValue: 'Wzmocnienie boczne' },
    '29': { x: 13, y: 55, position: 'bottom', displayValue: 'Kielich amortyzatora' },
    '30': { x: 50, y: 52, position: 'top', displayValue: 'Ściana grodziowa' },
    '31': { x: 88, y: 55, position: 'bottom', displayValue: 'Kielich amortyzatora' },
    '32': { x: 97.5, y: 51, position: 'top', displayValue: 'Wzmocnienie boczne' },
  },
  // STRONA PRAWA
  right: {
    '19': { x: 16, y: 38, position: 'top' },
    '21': { x: 51, y: 31, position: 'top' },
    '22': { x: 85, y: 40, position: 'top' },
    '17': { x: 6, y: 54, position: 'top' },
    '23': { x: 68, y: 52, position: 'top' },
    '24': { x: 85, y: 60, position: 'bottom' },
    '25': { x: 53, y: 72, position: 'bottom' },
    '15': { x: 7, y: 66, position: 'right' },
    // '25': { x: 47, y: 74, position: 'bottom' },
  },
};

/**
 * Mapowanie punktów dla vana
 *
 * Van ma nieco inny rozkład punktów ze względu na większe wymiary
 * Współrzędne dostosowane do proporcji vana
 */
const vanPaintPointsMap: VehiclePaintPointsMap = {
  // STRONA LEWA (zgodnie z proporcjami vana)
  left: {
    '6': { x: 48, y: 28, position: 'top' }, // Przednia część, góra
    '2': { x: 78, y: 33, position: 'left' }, // Przednia część, środek
    '7': { x: 18, y: 38, position: 'bottom' }, // Środek pojazdu
    '8': { x: 48, y: 48, position: 'bottom' }, // Tylna część, góra
    '4': { x: 97, y: 81, position: 'left' }, // Koło tylne, górna część
  },
  // WIDOK Z GÓRY (komora silnika)
  top: {
    '23': { x: 2.3, y: 51, position: 'top', 'displayValue': 'Wzmocnienie boczne' }, // 400-500 (żółty, lewy bok)
    '24': { x: 13, y: 55, position: 'bottom', displayValue: "Kielich amortyzatora" }, // 150-200 (żółty, lewy dół)
    '25': { x: 50, y: 52, position: 'top', displayValue: 'Ściana grodziowa' }, // 150-200 (żółty, środek góra)
    '26': { x: 88, y: 55, position: 'bottom', displayValue: "Kielich amortyzatora" }, // 50-100 (zielony, prawy dół)
    '27': { x: 97.5, y: 51, position: 'top', 'displayValue': 'Wzmocnienie boczne' }, // 150-200 (żółty, prawy bok)
  },
  // STRONA PRAWA (lustrzane odbicie lewej)
  right: {
    '21': { x: 83, y: 35, position: 'bottom' },
    '22': { x: 62, y: 28, position: 'top' },
    '15': { x: 25, y: 34, position: 'right' },
    '19': { x: 52, y: 49, position: 'bottom' },
    '20': { x: 45, y: 47.4, position: 'top' },
    '18': { x: 80, y: 57, position: 'bottom' },
    '17': { x: 50, y: 65.5, position: 'bottom' },
    '12': { x: 5, y: 59, position: 'top' },
    // '26': { x: 23, y: 69, position: 'right' },
    // '27': { x: 47, y: 74, position: 'bottom' },
  },
};

/**
 * Mapowanie punktów dla hatchbacka
 *
 * Hatchback ma krótszą sylwetkę, punkty są bardziej skupione
 */
const hatchbackPaintPointsMap: VehiclePaintPointsMap = {
  // STRONA LEWA
  left: {
    '2': { x: 83, y: 40, position: 'left' },
    '4': { x: 93, y: 53, position: 'left' },
    '6': { x: 63, y: 83, position: 'right' },
    '7': { x: 47, y: 33.7, position: 'top' },
    '8': { x: 25, y: 52, position: 'left' },
    '9': { x: 47, y: 63, position: 'bottom' },
    // '7': { x: 76, y: 69, position: 'left' },
    // '12': { x: 50, y: 73, position: 'bottom' },
  },
  // WIDOK Z GÓRY
  top: {
    '22': { x: 2.3, y: 51, position: 'top', displayValue: 'Wzmocnienie boczne' },
    '23': { x: 13, y: 55, position: 'bottom', displayValue: 'Kielich amortyzatora' },
    '24': { x: 50, y: 52, position: 'top', displayValue: 'Ściana grodziowa' },
    '25': { x: 88, y: 55, position: 'bottom', displayValue: 'Kielich amortyzatora' },
    '26': { x: 97.5, y: 51, position: 'top', displayValue: 'Wzmocnienie boczne' },
  },
  // STRONA PRAWA
  right: {
    '15': { x: 16, y: 39, position: 'right' },
    '13': { x: 4, y: 54, position: 'right' },
    '11': { x: 30, y: 81, position: 'left' },
    '17': { x: 58, y: 33.5, position: 'top' },
    '18': { x: 76, y: 50, position: 'right' },
    '19': { x: 52, y: 63.5, position: 'bottom' },
    // '26': { x: 68, y: 70, position: 'bottom' },
    // '27': { x: 82, y: 72, position: 'bottom' },
  },
};

/**
 * Pobiera mapowanie punktów dla danego typu pojazdu
 */
export const getVehiclePaintPointsMap = (
  vehicleType: VehicleType,
): VehiclePaintPointsMap => {
  switch (vehicleType) {
    case 'sedan':
      return sedanPaintPointsMap;
    case 'van':
      return vanPaintPointsMap;
    case 'hatchback':
      return hatchbackPaintPointsMap;
    default:
      return sedanPaintPointsMap;
  }
};

/**
 * Sprawdza czy punkt istnieje dla danego widoku i typu pojazdu
 */
export const hasPointForView = (
  vehicleType: VehicleType,
  view: 'left' | 'top' | 'right',
  pointNumber: PaintPointNumber,
): boolean => {
  const map = getVehiclePaintPointsMap(vehicleType);
  return map[view][pointNumber] !== undefined;
};

/**
 * Pobiera współrzędne punktu
 */
export const getPointCoordinates = (
  vehicleType: VehicleType,
  view: 'left' | 'top' | 'right',
  pointNumber: PaintPointNumber,
) => {
  const map = getVehiclePaintPointsMap(vehicleType);
  return map[view][pointNumber];
};
